import React from "react";
import { MdArrowForward } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useTable } from "react-table";
import tw from "tailwind-styled-components";
import Config from "../../../Config";

const LeadSourceTable = ({ leadSourceSummary = [] }) => {
    const navigate = useNavigate();

    // Source mapping from Config
    const source = [
        { value: Config.SOURCE.FACEBOOK, label: 'Facebook' },
        { value: Config.SOURCE.GOOGLE, label: 'Google' },
        { value: Config.SOURCE.INSTAGRAM, label: 'Instagram' },
        { value: Config.SOURCE.FLATMATE, label: 'Flatmate.in' },
        { value: Config.SOURCE.WALKIN, label: 'Walk In' },
        { value: Config.SOURCE.WEBSITE, label: 'Website' },
        { value: Config.SOURCE.MAGICBRICKS, label: 'MagicBricks' },
        { value: Config.SOURCE.ACRES, label: '99acres' },
        { value: Config.SOURCE.HOUSING, label: 'Housing' },
        { value: Config.SOURCE.NOBROKER, label: 'NoBroker' },
        { value: Config.SOURCE.OLX, label: 'Olx' },
        { value: Config.SOURCE.QUIKR, label: 'Quikr' },
        { value: Config.SOURCE.OTHERS, label: 'Others' },
    ];

    // Create a map for quick lookup
    const sourceMap = React.useMemo(() => {
        const map = {};
        source.forEach(item => {
            map[item.value] = item.label;
        });
        return map;
    }, []);

    const getSourceLabel = (sourceId) => {
        return sourceMap[sourceId] || `Source ${sourceId}`;
    };

    // Prepare data for the table
    const data = React.useMemo(() => {
        if (!Array.isArray(leadSourceSummary) || leadSourceSummary.length === 0) {
            return [];
        }

        // Sort by total leads descending
        return [...leadSourceSummary]
            .sort((a, b) => b.totalLeads - a.totalLeads)
            .map(item => ({
                ...item,
                sourceLabel: getSourceLabel(item.source),
                // Calculate conversion rate
                conversionRate: item.totalLeads > 0
                    ? Math.round((item.convertedLeads / item.totalLeads) * 100)
                    : 0
            }));
    }, [leadSourceSummary]);

    const columns = React.useMemo(
        () => [
            {
                Header: "Source",
                accessor: "sourceLabel",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                            {row.original.sourceLabel || "N/A"}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Leads",
                accessor: "totalLeads",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-gray-900">
                            {row.original.totalLeads || 0}
                        </div>
                    </div>
                ),
            },
            // {
            //     Header: "Active",
            //     accessor: "activeLeads",
            //     Cell: ({ row }) => (
            //         <div>
            //             <div className="text-sm font-medium text-green-600">
            //                 {row.original.activeLeads || 0}
            //             </div>
            //         </div>
            //     ),
            // },
            {
                Header: "Follow Ups",
                accessor: "pendingFollowUps",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-amber-600">
                            {row.original.pendingFollowUps || 0}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Visits",
                accessor: "pendingVisits",
                headerClassName: "text-center",
                className: "text-center",
                Cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-purple-600">
                            {row.original.pendingVisits || 0}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Converted",
                accessor: "conversionRate",
                headerClassName: "text-center",
                className: "text-center",

                Cell: ({ row }) => {
                    const converted = row.original.convertedLeads || 0;
                    const rate = row.original.conversionRate || 0;
                    return (
                        <div>
                            {/* <span className="text-sm font-medium text-emerald-600">
                                {converted}
                            </span> */}
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${rate >= 50 ? 'bg-green-100 text-green-700' :
                                rate >= 25 ? 'bg-amber-100 text-amber-700' :
                                    rate > 0 ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-500'
                                }`}>
                                {rate}%
                            </span>
                        </div>
                    );
                },

            },
        ],
        []
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({
            columns,
            data,
        });

    // Calculate total summary
    const totalSummary = React.useMemo(() => {
        if (!data || data.length === 0) {
            return {
                totalLeads: 0,
                activeLeads: 0,
                pendingFollowUps: 0,
                pendingVisits: 0,
                convertedLeads: 0,
                lostLeads: 0
            };
        }
        return data.reduce((acc, item) => ({
            totalLeads: acc.totalLeads + (item.totalLeads || 0),
            activeLeads: acc.activeLeads + (item.activeLeads || 0),
            pendingFollowUps: acc.pendingFollowUps + (item.pendingFollowUps || 0),
            pendingVisits: acc.pendingVisits + (item.pendingVisits || 0),
            convertedLeads: acc.convertedLeads + (item.convertedLeads || 0),
            lostLeads: acc.lostLeads + (item.lostLeads || 0),
        }), {
            totalLeads: 0,
            activeLeads: 0,
            pendingFollowUps: 0,
            pendingVisits: 0,
            convertedLeads: 0,
            lostLeads: 0
        });
    }, [data]);

    return (
        <Container>
            {/* Header */}
            <Header>
                <Title>Lead Source Performance</Title>
                {data.length > 0 && (
                    <SourceCount>
                        {data.length} {data.length === 1 ? 'source' : 'sources'}
                    </SourceCount>
                )}
            </Header>

            {/* Table */}
            <TableWrapper>
                <Table {...getTableProps()}>
                    <Thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                {headerGroup.headers.map((column) => (
                                    <Th {...column.getHeaderProps()} className={column.headerClassName} key={column.id}>
                                        {column.render("Header")}
                                    </Th>
                                ))}
                            </tr>
                        ))}
                    </Thead>

                    <Tbody {...getTableBodyProps()}>
                        {rows.length > 0 ? (
                            rows.map((row) => {
                                prepareRow(row);
                                return (
                                    <Tr
                                        {...row.getRowProps()}
                                        key={row.id}
                                    >
                                        {row.cells.map((cell) => (
                                            <Td {...cell.getCellProps()} className={cell.column.className} key={cell.column.id}>
                                                {cell.render("Cell")}
                                            </Td>
                                        ))}
                                    </Tr>
                                );
                            })
                        ) : (
                            <tr>
                                <Td colSpan={8} className="text-center py-6 text-gray-400">
                                    No lead source data found
                                </Td>
                            </tr>
                        )}
                    </Tbody>

                    {/* Footer with totals */}
                    {/* {rows.length > 0 && (
                        <Tfoot>
                            <tr>
                                <Td className="font-semibold text-gray-700">Total</Td>
                                <Td className="font-semibold text-gray-900">{totalSummary.totalLeads}</Td>
                                <Td className="font-semibold text-green-600">{totalSummary.activeLeads}</Td>
                                <Td className="font-semibold text-amber-600">{totalSummary.pendingFollowUps}</Td>
                                <Td className="font-semibold text-purple-600">{totalSummary.pendingVisits}</Td>
                                <Td className="font-semibold text-emerald-600">{totalSummary.convertedLeads}</Td>
                                <Td className="font-semibold text-red-600">{totalSummary.lostLeads}</Td>
                                <Td className="font-semibold text-gray-700">
                                    {totalSummary.totalLeads > 0
                                        ? Math.round((totalSummary.convertedLeads / totalSummary.totalLeads) * 100)
                                        : 0}%
                                </Td>
                            </tr>
                        </Tfoot>
                    )} */}
                </Table>
            </TableWrapper>
        </Container>
    );
};

const Container = tw.div`
  bg-white rounded-lg p-5
`;

const Header = tw.div`
  flex items-center justify-between mb-4
`;

const Title = tw.h3`
  text-sm font-semibold text-gray-700 uppercase tracking-wide
`;

const SourceCount = tw.span`
  text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full
`;

const TableWrapper = tw.div`
  overflow-x-auto
`;

const Table = tw.table`
  min-w-full border-separate border-spacing-y-1
`;

const Thead = tw.thead``;

const Th = tw.th`
  bg-gray-100 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide
`;

const Tbody = tw.tbody``;

// const Tr = tw.tr`
//   hover:bg-gray-50
//   transition-colors
// `;

// const Td = tw.td`
//   px-4 py-3 text-sm text-gray-700
// `;
const Tr = tw.tr`
  border-b
  border-gray-100
  hover:bg-sky-50
  transition-colors
`;

const Td = tw.td`
  px-4
  py-3
  text-sm
  text-gray-700
`;

const Tfoot = tw.tfoot`
  bg-gray-50 border-t-2 border-gray-200
`;

export default LeadSourceTable;